import React, { useEffect, useRef } from 'react';
import '../Pages/styles.css'; // Import the prefixed CSS as requested

// --- Import FontAwesomeIcon Component ---
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// --- Import ALL Necessary Icons from Font Awesome (Updated for Master Planning Marketing) ---
import {
    // Core Concepts & General
    faSitemap,          // Master Planning / Campus Design
    faBuilding,         // Facility / Infrastructure / Architecture
    faLightbulb,        // Vision / Strategy / Innovation
    faBalanceScale,     // Balancing Art & Science / Considerations
    faCheckCircle,      // Quality / Compliance / Success
    faTasks,            // Functionality / Operations / Efficiency
    faHeartPulse,       // Healing Environments / Patient Satisfaction
    faUsersCog,         // Team / Expertise / Stakeholders
    faDraftingCompass,  // Design / Blueprints
    faMapMarkedAlt,     // Site / Location
    faProjectDiagram,   // Flow / Interdepartmental Planning
    faTools,            // Construction / Physical Setup
    faClipboardList,    // Assessment / Evaluation / Roadmap
    faSearchLocation,   // Site Evaluation
    faRoute,            // Circulation / Flow Analysis
    faShieldAlt,        // Safety / Compliance
    // Keep other potentially useful icons if needed
    // Icons likely unused from previous versions:
    /*
    faBullhorn, faChartLine, faUsers, faStar, faPrint, faLaptopCode, faMousePointer,
    faNewspaper, faCalendarCheck, faCrosshairs, faNetworkWired, faTasks, faUserTie,
    faBoxOpen, faComments, faTags, faSearchDollar, faSmileBeam, faFileInvoiceDollar,
    faReceipt, faStream, faSyncAlt, faDollarSign, faMedal
    */
} from '@fortawesome/free-solid-svg-icons'; // Using free solid icons


// --- Intersection Observer Hooks (No changes needed in hook logic) ---
const useIntersectionObserverTitle = (options) => {
    const containerRef = useRef(null);
    const [isVisible, setIsVisible] = React.useState(false);
    const callbackFunction = (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) setIsVisible(true);
    };
    useEffect(() => {
        const observer = new IntersectionObserver(callbackFunction, options);
        const currentRef = containerRef.current;
        if (currentRef) observer.observe(currentRef);
        return () => { if (currentRef) observer.unobserve(currentRef); };
    }, [containerRef, options]);
    return [containerRef, isVisible];
};

const useIntersectionObserverElements = (options) => {
    useEffect(() => {
        const observerCallback = (entries, observer) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    const target = entry.target;
                    const parent = target.parentElement;
                    const siblings = parent ? Array.from(parent.children).filter(el => el.classList.contains('cliniqor-fade-in-up')) : [target];
                    const index = siblings.indexOf(target);
                    const staggerIndex = index >= 0 ? index : 0;

                    target.style.setProperty('--item-index', staggerIndex % 8); // Keep stagger logic
                    target.classList.add('cliniqor-visible'); // Use prefixed class
                    observer.unobserve(target);
                }
            });
        };
        const observer = new IntersectionObserver(observerCallback, options);
        const targets = document.querySelectorAll('.cliniqor-fade-in-up');
        targets.forEach(target => observer.observe(target));
        return () => targets.forEach(target => { if (target) observer.unobserve(target); });
    }, [options]);
};
// --- End Observer Hooks ---


function App() {

    // --- Observer Setup for Titles (Adjusted Refs) ---
    const titleObserverOptions = { threshold: 0.2 };
    const [title1Ref, title1Visible] = useIntersectionObserverTitle(titleObserverOptions); // The Art & Science Intro
    const [title2Ref, title2Visible] = useIntersectionObserverTitle(titleObserverOptions); // Complexity/Expertise Section
    const [title3Ref, title3Visible] = useIntersectionObserverTitle(titleObserverOptions); // Consulting Process Title
    // No specific title refs needed for each phase within the list

    // --- Observer Setup for General Fade-In Elements ---
    useIntersectionObserverElements({ threshold: 0.1 });

    // Choose a relevant hero background image for planning/design/architecture
    const heroBgImage = 'https://images.unsplash.com/photo-1606744824163-985d376605aa?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1920&q=80'; // Architecture/blueprint image

  return(
    // Use prefixed base wrapper class
    <div className="cliniqor-app-wrapper">

      {/* --- HERO SECTION --- */}
      <header
        className="cliniqor-hero-section" // Prefixed
        style={{ backgroundImage: `url(${heroBgImage})` }}
      >
        <div className="cliniqor-container"> {/* Prefixed */}
          <div className="cliniqor-hero-content"> {/* Prefixed */}
            {/* --- NEW HERO CONTENT --- */}
            <h1>Master Planning & Campus Design</h1>
            <p className="cliniqor-sub-heading">
              Strategic Growth Solutions by Cliniqor Healthcare Consultancy
            </p>
            <p className="cliniqor-intro-text">
              We take a long-term, strategic approach to healthcare design, creating sustainable, adaptable facilities that thrive amidst evolving challenges, ensuring patient satisfaction and operational efficiency.
            </p>
            {/* --- END NEW HERO CONTENT --- */}
          </div>
        </div>
      </header>

      <main>
        {/* --- THE ART & SCIENCE INTRO --- */}
        <section className="cliniqor-section cliniqor-section-first cliniqor-section-bg-gradient cliniqor-intro-section">
          <div className="cliniqor-container">
            <div ref={title1Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title1Visible ? 'cliniqor-visible' : ''}`}>
              <h2>Balancing Science and Art in Healthcare Design</h2>
              <p>Creating environments that heal, function efficiently, and inspire.</p>
            </div>
             <div className="cliniqor-intro-grid"> {/* Keeping grid structure */}
                 <div className="cliniqor-intro-text-content cliniqor-fade-in-up">
                     {/* --- NEW INTRO CONTENT --- */}
                     <p>Our approach balances the science (strict regulations, functional relationships, efficiency, scalability) with the art (envisioning healing spaces, creativity, innovation). We understand how the physical environment influences care, staff performance, and the overall hospital experience.</p>
                     <p>Great design supports long-term health and operational success. We focus on sustainable, adaptable designs anticipating future trends, ensuring every element contributes to enhanced outcomes, efficiency, and satisfaction.</p>
                     {/* --- END NEW INTRO CONTENT --- */}
                 </div>
             </div>
          </div>
        </section>

        {/* --- COMPLEXITY & EXPERTISE (Features Section - Optional, or keep as text) --- */}
        <section className="cliniqor-section cliniqor-features-section cliniqor-section-bg-light">
          <div className="cliniqor-container">
            <div ref={title2Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title2Visible ? 'cliniqor-visible' : ''}`}>
              {/* --- NEW SECTION TITLE --- */}
              <h2>Expertise for Complex Healthcare Environments</h2>
              <p>More than blueprints - a comprehensive strategy is essential.</p>
              {/* --- END NEW SECTION TITLE --- */}
            </div>
             <div className="cliniqor-text-content cliniqor-fade-in-up">
                 <p>Creating a healthcare campus requires domain expertise, extensive experience, and deep understanding of trends and regulations. It's about strategic planning for layout, staffing, technology, and sustainability.</p>
                 <p>Our team includes experts in healthcare engineering, architecture, and facility planning, ensuring precise execution. We craft functional, innovative, forward-thinking designs adaptable to future trends.</p>
                 <p>The final master plan is a strategic roadmap detailing space, budget, financial allocations, and long-term projections, aligning all stakeholders for long-term success.</p>
             </div>
            {/* Alternative: Could use Feature Cards here to highlight expertise areas if preferred */}
          </div>
        </section>

        {/* --- CONSULTING PROCESS (Detailed Planning Section - 4 Phases) --- */}
        <section className="cliniqor-section cliniqor-section-bg-texture cliniqor-detailed-planning-section"> {/* Kept existing classes */}
          <div className="cliniqor-container">
             <div ref={title3Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title3Visible ? 'cliniqor-visible' : ''}`}>
              {/* --- NEW SECTION TITLE --- */}
              <h2>Our Healthcare Consulting Process for Master Planning</h2>
               <p>A systematic, structured approach ensuring strategic alignment and operational excellence.</p>
               {/* --- END NEW SECTION TITLE --- */}
            </div>
            {/* Using Planning Items for the 4 Phases */}
            <div className="cliniqor-planning-items-wrapper">
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faLightbulb} /></div>
                    <div className="cliniqor-content"><h4>Phase I: Strategic Planning & Assessment</h4> <p>Aligning stakeholder vision with blueprint, strategic business assessment (financials, operations, trends), statistical analysis (demographics, demand), initial space planning & energy conservation, staffing requirement analysis.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faMapMarkedAlt} /></div>
                    <div className="cliniqor-content"><h4>Phase II: Conceptual Site Planning</h4> <p>Assessing physical site (accessibility, infrastructure), environmental factors (weather, soil, seismic), patient/visitor flow patterns, conceptual architectural drawings, facility design, land-use/zoning compliance.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faProjectDiagram} /></div>
                    <div className="cliniqor-content"><h4>Phase III: Interdepartmental Planning & Design</h4> <p>Meticulous internal layout planning, functional flow design between departments (ER, OT, ICU, labs, support), vertical transport planning, corridor optimization, critical zone location for privacy/infection control.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faTools} /></div>
                    <div className="cliniqor-content"><h4>Phase IV: Physical Set-Up, Evaluation & Recommendations</h4> <p>Overseeing construction per specifications/safety/regulations, regular evaluation of work-in-progress, addressing issues/delays, recommendations for modifications, final review & operational recommendations.</p> </div>
                 </div>
            </div>
          </div>
        </section>

       {/* Removed other specific sections like Why Choose Us grids, Service lists etc. as they weren't explicitly in this source text */}

      </main>

      {/* --- FOOTER CTA --- */}
      <footer className="cliniqor-section cliniqor-section-narrow cliniqor-footer-cta">
        <div className="cliniqor-container cliniqor-fade-in-up">
          {/* --- UPDATED FOOTER CONTENT --- */}
          <h3>Design the Future of Healing with Cliniqor</h3>
          <p>Partner with us to create exceptional healthcare environments that are sustainable, adaptable, patient-centric, and operationally efficient. Let's shape the future of healthcare strategically.</p>
          <a href="#contact" className="cliniqor-cta-button">Start Your Master Plan</a>
          {/* --- END UPDATED FOOTER CONTENT --- */}
        </div>
      </footer>
    </div> // End cliniqor-app-wrapper
  );
}

export default App;